/**
 * Pure windowed and cumulative series math over ordered (x, y) numeric arrays.
 *
 * `xs` is the ordering axis — epoch-ms for time series, or any monotonically
 * non-decreasing numeric axis. `ys` are the values. Both arrays are assumed
 * pre-ordered by `xs` ascending and of equal length. `NaN` in `ys` marks a gap
 * (a missing/non-numeric sample); each kernel documents how it treats gaps.
 *
 * Engine glue (Cell↔number coercion, partition framing, scatter-back to input
 * row order, duration parsing) lives in the expression runtime — these
 * functions stay free of any engine, arquero, or date dependency so they
 * remain reusable and unit-testable in isolation.
 *
 * `xs[k] as number` casts are safe: callers pass equal-length, dense arrays
 * indexed within bounds — TS can't prove the lengths match, so the cast is the
 * boundary acknowledgement (matching the runtime's indexed-access style).
 */
import { linearRegression, standardDeviation } from 'simple-statistics';

/** Reducers a rolling window can apply over its in-window values. */
export type RollingReducer = 'mean' | 'sum' | 'min' | 'max' | 'stddev';

/**
 * Window extent: either the last `count` samples (row-count window) or every
 * sample within a half-open span on the `xs` axis ending at the current sample
 * (`(xs[k] - ms, xs[k]]`). For time series `ms` is a duration in milliseconds.
 */
export type WindowExtent =
  | { readonly unit: 'rows'; readonly count: number }
  | { readonly unit: 'duration'; readonly ms: number };

/** Exponential-smoothing decay: a fixed per-step factor, or a time-based half-life. */
export type EwmaDecay =
  | { readonly kind: 'alpha'; readonly alpha: number }
  | { readonly kind: 'halflife'; readonly ms: number };

/** Lower index (inclusive) of the window ending at position `k`. `xs` ascending. */
const windowLo = (xs: readonly number[], k: number, extent: WindowExtent): number => {
  if (extent.unit === 'rows') {
    return Math.max(0, k - extent.count + 1);
  }
  const minX = (xs[k] as number) - extent.ms;
  let lo = k;
  while (lo > 0 && (xs[lo - 1] as number) > minX) {
    lo -= 1;
  }
  return lo;
};

/** Per-reducer kernel over a window's non-gap values. `stddev` is population
 * stddev (÷n), matching `numeric-stats.computeNumericStats`. */
const REDUCE_WINDOW: Record<RollingReducer, (win: readonly number[]) => number> = {
  sum: (win) => win.reduce((s, n) => s + n, 0),
  mean: (win) => win.reduce((s, n) => s + n, 0) / win.length,
  min: (win) => Math.min(...win),
  max: (win) => Math.max(...win),
  stddev: (win) => standardDeviation(win),
};

/** Collect the non-gap values in `[lo, k]`. */
const windowValues = (ys: readonly number[], lo: number, k: number): number[] => {
  const win: number[] = [];
  for (let j = lo; j <= k; j += 1) {
    const v = ys[j];
    if (v !== undefined && !Number.isNaN(v)) {
      win.push(v);
    }
  }
  return win;
};

/**
 * Rolling reduction. Each position reduces the non-gap values in its trailing
 * window; an all-gap (empty) window yields `null`.
 */
export const rollingWindow = (
  xs: readonly number[],
  ys: readonly number[],
  extent: WindowExtent,
  reducer: RollingReducer,
): (number | null)[] =>
  ys.map((_, k) => {
    const win = windowValues(ys, windowLo(xs, k, extent), k);
    return win.length === 0 ? null : REDUCE_WINDOW[reducer](win);
  });

/**
 * Rolling least-squares slope over the trailing window, scaled to `scale`
 * x-units (e.g. pass milliseconds-per-day to get a per-day slope from an
 * epoch-ms axis). Windows with fewer than two non-gap points yield `null`.
 */
export const rollingSlope = (
  xs: readonly number[],
  ys: readonly number[],
  extent: WindowExtent,
  scale: number,
): (number | null)[] =>
  ys.map((_, k) => {
    const lo = windowLo(xs, k, extent);
    const pts: [number, number][] = [];
    for (let j = lo; j <= k; j += 1) {
      const y = ys[j];
      const x = xs[j];
      if (x !== undefined && y !== undefined && !Number.isNaN(y)) {
        pts.push([x, y]);
      }
    }
    if (pts.length < 2) {
      return null;
    }
    const { m } = linearRegression(pts);
    return Number.isFinite(m) ? m * scale : null;
  });

/** Running-reduction step per cumulative op. */
const CUMULATIVE_COMBINE: Record<'sum' | 'max' | 'min', (a: number, b: number) => number> = {
  sum: (a, b) => a + b,
  max: Math.max,
  min: Math.min,
};

/**
 * Running reduction (cumulative sum / max / min). Gaps carry the prior running
 * value forward; positions before the first non-gap sample yield `null`.
 */
export const cumulative = (ys: readonly number[], op: 'sum' | 'max' | 'min'): (number | null)[] => {
  const combine = CUMULATIVE_COMBINE[op];
  let acc: number | null = null;
  return ys.map((y) => {
    if (Number.isNaN(y)) {
      return acc;
    }
    acc = acc === null ? y : combine(acc, y);
    return acc;
  });
};

/**
 * Running trapezoidal integral of `ys` with respect to `xs`, divided by
 * `scale`. For a flow rate sampled in value-per-unit over an epoch-ms axis,
 * pass `scale = milliseconds-per-unit` to obtain a cumulative volume in the
 * value's base unit. The integral starts at 0 at the first non-gap sample;
 * gaps carry the prior cumulative value forward.
 */
export const trapezoidIntegral = (xs: readonly number[], ys: readonly number[], scale: number): (number | null)[] => {
  let acc: number | null = null;
  let prevX = Number.NaN;
  let prevY = Number.NaN;
  return ys.map((y, k) => {
    if (Number.isNaN(y)) {
      return acc;
    }
    const x = xs[k] as number;
    if (acc === null) {
      acc = 0;
    } else {
      acc += (0.5 * (y + prevY) * (x - prevX)) / scale;
    }
    prevX = x;
    prevY = y;
    return acc;
  });
};

/**
 * Exponential moving average. With a fixed `alpha`, each step blends
 * `alpha·y + (1-alpha)·prev`. With a `halflife` (ms), the blend factor adapts
 * to the elapsed `xs` gap so irregularly-sampled series decay by wall-clock
 * time rather than by sample count. Gaps carry the prior EMA forward.
 */
export const exponentialMovingAverage = (
  xs: readonly number[],
  ys: readonly number[],
  decay: EwmaDecay,
): (number | null)[] => {
  let ema: number | null = null;
  let prevX = Number.NaN;
  return ys.map((y, k) => {
    if (Number.isNaN(y)) {
      return ema;
    }
    const x = xs[k] as number;
    if (ema === null) {
      ema = y;
      prevX = x;
      return ema;
    }
    const alpha = decay.kind === 'alpha' ? decay.alpha : 1 - 2 ** -((x - prevX) / decay.ms);
    ema += alpha * (y - ema);
    prevX = x;
    return ema;
  });
};

/**
 * First difference (`delta`) or fractional change (`pct`) against the
 * immediately preceding row. The first row, and any row adjacent to a gap,
 * yield `null`; `pct` also yields `null` when the prior value is 0.
 */
export const discreteDifference = (ys: readonly number[], mode: 'delta' | 'pct'): (number | null)[] => {
  let prev: number | undefined;
  return ys.map((y) => {
    const p = prev;
    prev = y;
    if (p === undefined || Number.isNaN(y) || Number.isNaN(p)) {
      return null;
    }
    if (mode === 'delta') {
      return y - p;
    }
    return p === 0 ? null : (y - p) / p;
  });
};

/**
 * Discrete rate of change (Δy/Δx) against the preceding row, scaled to `scale`
 * x-units (pass milliseconds-per-unit for a per-unit-time rate). The first
 * row, rows adjacent to a gap, and zero-Δx steps yield `null`.
 */
export const rateOfChange = (xs: readonly number[], ys: readonly number[], scale: number): (number | null)[] => {
  let prevX: number | undefined;
  let prevY: number | undefined;
  return ys.map((y, k) => {
    const x = xs[k] as number;
    const out =
      prevX === undefined || prevY === undefined || Number.isNaN(y) || Number.isNaN(prevY) || x - prevX === 0
        ? null
        : (y - prevY) / ((x - prevX) / scale);
    prevX = x;
    prevY = y;
    return out;
  });
};
